import type { IPitcherClient } from "@codesandbox/pitcher-client";
import { Disposable } from "@codesandbox/pitcher-common";
import { createEditorInputFromTab } from "components/Layout/Panels/VSCode/editorSync";
import { onExtensionsInitialized } from "environment-interface/monacoEditor/browser/services/editor/vscode/monaco";
import type { Tab, TabParams } from "features/types/tabs";
import * as vscode from "vscode";
import { Registry } from "vscode/monaco";
import type {
  EditorInput,
  IEditorOptions,
  IUntypedEditorInput,
} from "vscode/services";
import {
  IEditorService,
  StandaloneServices,
  IEditorGroupsService,
  IInstantiationService,
} from "vscode/services";

import { safeLocalStorage } from "utils/safeLocalStorage";

import type { IEditorFactoryRegistry } from "./vscode-type";

const EDITOR_PANEL_ASSIGNED_TAB_TYPES: Array<Tab["type"]> = ["DIFF", "FILE"];

export const IGNORED_TAB_TYPES: Set<Tab["type"]> = new Set([
  "PR_INFO",
  "TASK_LOG",
  "TERMINAL",
  "NEW_TERMINAL",
]);

/**
 * Tabs that should not open as "preview" (e.g. when someone opens a file it's automatically
 * closed)
 */
const DEFAULT_PINNED_TABS: Set<Tab["type"]> = new Set([
  "ENV_SETUP",
  "SETUP_TASKS",
  "CHANGELOG",
  "DOCS",
  "SANDBOX_INFO",
  "VIRTUAL_MACHINE",
]);

export class DevtoolWindowManager extends Disposable {
  private editorGroupsService: IEditorGroupsService;
  private editorService: IEditorService;
  private instanceId: string;

  public devtoolWindowVisible: boolean;
  private cachedWindowDevtoolId: number | undefined = undefined;
  constructor(private pitcher: IPitcherClient) {
    super();

    this.instanceId = this.pitcher.instanceId;
    this.editorGroupsService = StandaloneServices.get(IEditorGroupsService);
    this.editorService = StandaloneServices.get(IEditorService);
    this.devtoolWindowVisible = this.isDevtoolWindowVisible();

    // Optimistically find the window devtool based on the lock property
    this.cachedWindowDevtoolId = this.editorGroupsService
      .getGroups(2)
      .find((item) => item.isLocked)?.id;

    this.addDisposable(
      this.editorGroupsService.onDidAddGroup(() => {
        const hasDevtoolWindow = this.isDevtoolWindowVisible();

        if (this.devtoolWindowVisible !== hasDevtoolWindow) {
          this.devtoolWindowVisible = hasDevtoolWindow;
        }
      }),
    );
    this.addDisposable(
      this.editorGroupsService.onDidRemoveGroup(() => {
        const hasDevtoolWindow = this.isDevtoolWindowVisible();

        if (this.devtoolWindowVisible !== hasDevtoolWindow) {
          this.devtoolWindowVisible = hasDevtoolWindow;
        }
      }),
    );
    this.addDisposable(
      this.editorGroupsService.onDidMoveGroup(() => {
        const hasDevtoolWindow = this.isDevtoolWindowVisible();

        if (this.devtoolWindowVisible !== hasDevtoolWindow) {
          this.devtoolWindowVisible = hasDevtoolWindow;
        }
      }),
    );

    this.addDisposable(
      this.editorService.onDidCloseEditor((e) => {
        const group = this.editorGroupsService.getGroup(e.groupId);
        if (!group) {
          return;
        }

        // If a group ends up with no open editors, make sure the lock is off,
        // so the group can properly be disposed.
        if (group.isEmpty && group.id === this.cachedWindowDevtoolId) {
          group.lock(false);

          // it's no longer a devtool window
          this.cachedWindowDevtoolId = undefined;

          this.updateDevtoolWindowVisiblityStatus();
          if (!this.isHidingDevtoolWindow) {
            // If we're closing the last editor by pressing the "x" next to the editor,
            // we should not open it again the next time we open the devtool group. So
            // we should serialize that there are no devtool windows.
            // If we _hide_ the devtool window (using the button in the toolbar or the
            // keyboard shortcut), we should remember what was in the group and resolve
            // it the next time we open the devtool window again.
            this.serializeDevtoolWindows();
          }
          this.isHidingDevtoolWindow = false;
        }
      }),
    );

    this.addDisposable(
      this.editorService.onWillOpenEditor((e) => {
        const group = this.editorGroupsService.getGroup(e.groupId);
        const [firstGroup, secondGroup] = this.editorGroupsService.getGroups(2);

        const firstGroupIsDevtool =
          firstGroup?.id === this.cachedWindowDevtoolId;
        const secondGroupIsNew = secondGroup?.id === group?.id;

        /**
         * When the devtool windown is full screen and the user opens a new editor
         * we want to open the file in the left side and move the devtool window to the right
         */
        if (
          firstGroupIsDevtool &&
          secondGroupIsNew &&
          group.editors.length === 0
        ) {
          this.editorGroupsService.moveGroup(group, firstGroup, 2);

          return;
        }
      }),
    );

    this.openDefaultPreviewWindows();

    onExtensionsInitialized(() => {
      const existingData = safeLocalStorage.get(this.getLocalStorageId());

      if (existingData !== null) {
        return;
      }

      const devtoolWindow = this.getDevtoolWindow();
      if (devtoolWindow && devtoolWindow.editors.length === 0) {
        this.showDevtoolWindow();
      }
    });
  }

  private isDevtoolWindowVisible() {
    const devtoolWindow = this.getDevtoolWindow();

    return Boolean(
      devtoolWindow &&
        this.editorGroupsService.getSize(devtoolWindow).width > 0 &&
        devtoolWindow.editors.length > 0,
    );
  }

  private updateDevtoolWindowVisiblityStatus() {
    const hasDevtoolWindow = this.isDevtoolWindowVisible();

    if (this.devtoolWindowVisible !== hasDevtoolWindow) {
      this.devtoolWindowVisible = hasDevtoolWindow;
    }
  }

  private getDevtoolWindow() {
    const allGroups = this.editorGroupsService.getGroups(2);

    // Get cached window
    if (this.cachedWindowDevtoolId) {
      const cachedDevtoolWindow = allGroups.find(
        (w) => w.id === this.cachedWindowDevtoolId,
      );

      if (cachedDevtoolWindow) {
        return cachedDevtoolWindow;
      }
    }

    if (this.editorGroupsService.getGroups(2).length < 2) {
      return undefined;
    }

    // Considering that there are two panel, this will get the second one
    const devtoolWindow = this.editorGroupsService.findGroup(
      {
        location: 1,
      },
      undefined,
      false,
    );

    if (!devtoolWindow) {
      return undefined;
    }

    devtoolWindow.lock(true);

    // Save the window id, so we can easily find this group
    this.cachedWindowDevtoolId = devtoolWindow.id;

    return devtoolWindow;
  }

  private async openEditorInDevtoolWindow(
    input: IUntypedEditorInput,
  ): Promise<void> {
    // Try to open in the side group (dedicated for devtools)
    const devtoolWindow = this.getDevtoolWindow();
    if (devtoolWindow) {
      await this.editorService.openEditor(input, devtoolWindow);
    } else {
      // No devtool window, so tell VSCode to open to the side (-2 = SIDE_GROUP)
      await this.editorService.openEditor(input, -2);

      // Lock the newly created side group
      const sideGroup = this.getDevtoolWindow();
      if (sideGroup) {
        sideGroup.lock(true);
      }
      this.updateDevtoolWindowVisiblityStatus();
    }
  }

  public async openNewDevtoolTab() {
    await this.openEditorInDevtoolWindow({
      resource: vscode.Uri.from({
        scheme: "csb-new-tab",
        path: "/",
      }),
    });
  }

  public async showDevtoolWindow() {
    const devtoolWindow = this.getDevtoolWindow();

    if (!devtoolWindow || !this.isDevtoolWindowVisible()) {
      this.openNewDevtoolTab();

      this.restoreDevtoolWindows();
    } else {
      this.editorGroupsService.setSize(devtoolWindow, {
        width: this.editorGroupsService.mainPart.contentDimension.width / 2,
        height: this.editorGroupsService.getSize(devtoolWindow).height,
      });
    }

    this.updateDevtoolWindowVisiblityStatus();
  }

  private isHidingDevtoolWindow = false;
  public hideDevtoolWindow() {
    const devtoolWindow = this.getDevtoolWindow();
    if (devtoolWindow) {
      this.isHidingDevtoolWindow = true;
      this.serializeDevtoolWindows();
      devtoolWindow.closeAllEditors();
      this.updateDevtoolWindowVisiblityStatus();
    }
  }

  public async openTabFromAction(tab: TabParams, options?: IEditorOptions) {
    const isEditorTab = EDITOR_PANEL_ASSIGNED_TAB_TYPES.includes(tab.type);
    const editorInput = createEditorInputFromTab(tab);
    if (!editorInput) {
      return;
    }

    if (IGNORED_TAB_TYPES.has(tab.type)) {
      // Don't open these, VSCode has an alternative for those
      return;
    }

    if (options) {
      editorInput.options = options;
    }

    const isPinned = DEFAULT_PINNED_TABS.has(tab.type);
    if (isPinned && editorInput.options?.pinned == null) {
      editorInput.options = {
        ...(editorInput.options || {}),
        pinned: true,
      };
    }

    if (isEditorTab) {
      // Open in active group
      await this.editorService.openEditor(editorInput);
    } else {
      await this.openEditorInDevtoolWindow(editorInput);
    }
  }

  public setInstanceId(instanceId: string) {
    this.copyWindowData(this.instanceId, instanceId);
    this.instanceId = instanceId;
  }

  private getLocalStorageId(instanceId = this.instanceId) {
    return `CSB/${instanceId}`;
  }

  private lastEditorInputs: EditorInput[] | null = null;
  private serializeDevtoolWindows() {
    const devtoolWindow = this.getDevtoolWindow();

    if (!devtoolWindow) {
      return;
    }

    const editorInputFactories = Registry.as<IEditorFactoryRegistry>(
      "workbench.contributions.editor.inputFactories",
    );
    const serializedEditors = devtoolWindow.editors
      .map((editor) => {
        const serializer = editorInputFactories.getEditorSerializer(editor);

        const serialized = serializer?.serialize(editor);

        return { typeId: editor.typeId, serialized };
      })
      .filter(
        (serializedEditor) => serializedEditor.serialized != null,
      ) as SerializedEditor[];

    this.lastEditorInputs?.forEach((editor) => editor.dispose());
    this.lastEditorInputs = devtoolWindow.editors.map((editor) =>
      editor.copy(),
    );

    safeLocalStorage.set(
      this.getLocalStorageId(),
      JSON.stringify(serializedEditors),
    );
  }

  private restoreDevtoolWindows() {
    // If we have the editor inputs in memory, we restore them from memory.
    const devtoolWindow = this.getDevtoolWindow();

    if (this.lastEditorInputs) {
      return this.editorService.openEditors(
        this.lastEditorInputs,
        devtoolWindow,
      );
    }

    // If we don't have that, but we do have the serialized editors, we
    // recover from that
    const storedEditors = safeLocalStorage.get(this.getLocalStorageId());

    if (storedEditors) {
      const instantiationService = StandaloneServices.get(
        IInstantiationService,
      );
      const editorInputFactories = Registry.as<IEditorFactoryRegistry>(
        "workbench.contributions.editor.inputFactories",
      );

      const serializedEditors = JSON.parse(storedEditors) as SerializedEditor[];
      const editors = serializedEditors
        .map((serializedEditor) => {
          const serializer = editorInputFactories.getEditorSerializer(
            serializedEditor.typeId,
          );

          return serializer?.deserialize(
            instantiationService,
            serializedEditor.serialized,
          );
        })
        .filter(Boolean) as EditorInput[];

      return this.editorService.openEditors(editors, devtoolWindow);
    }
  }

  private async openDefaultPreviewWindows() {
    const storedEditors = safeLocalStorage.get(this.getLocalStorageId());

    // use cache instead
    if (storedEditors) {
      this.restoreDevtoolWindows();

      return;
    }

    const devtoolWindow = this.getDevtoolWindow();
    const editors: IUntypedEditorInput[] = [];

    if (this.pitcher.type === "browser") {
      const editor = createEditorInputFromTab({
        type: "UNASSIGNED_PORT",
        port: 0,
      });

      if (editor) {
        editors.push(editor);
      }
    } else if (this.pitcher.type === "vm") {
      const tasks = this.pitcher.clients.task.getTasks().tasks;
      const tasksWithPorts = Object.values(tasks).filter(
        (t) => t.shell?.status === "RUNNING" && t.ports.length > 0,
      );

      for (const task of tasksWithPorts) {
        if (task.unconfigured) {
          // If the task is not configured, ignore
          continue;
        }

        for (const port of task.ports) {
          const taskHasPreview = task.preview;
          const isPreviewPort = task.preview && port.port === task.preview.port;

          if (!isPreviewPort && taskHasPreview) {
            // If the task has a port configured, and this is not the configured
            // port, don't open it
            continue;
          }

          if (!task.preview && port.port > 10000) {
            // Ports above 10000 are usually ports we don't want to highlight. Things
            // like devtool ports or websocket ports.
            continue;
          }

          const editor = createEditorInputFromTab({
            type: "TASK_PORT",
            port: port.port,
            taskId: task.id,
          });

          if (editor) {
            editors.push(editor);
          }
        }
      }
    }

    if (editors.length > 0 && devtoolWindow) {
      this.editorService.openEditors(editors, devtoolWindow);
    }
  }

  private copyWindowData(fromInstanceId: string, toInstanceId: string) {
    this.serializeDevtoolWindows();
    const localStorageId = this.getLocalStorageId(fromInstanceId);
    const fromLocalStorageValue = safeLocalStorage.get(localStorageId);
    if (fromLocalStorageValue === null) {
      return;
    }

    safeLocalStorage.set(
      this.getLocalStorageId(toInstanceId),
      fromLocalStorageValue,
    );
  }
}

type SerializedEditor = { typeId: string; serialized: string };
